import React, { useMemo } from 'react';
import { FiPlus, FiHeart, FiGlobe, FiDollarSign, FiZap } from 'react-icons/fi';
import { ProjectTile } from '../domain/types';
import { techLabel } from '../domain/techLabel';
import { useAuth } from '../../../auth/AuthProvider';
import { useImpactData } from '../../../hooks/useImpactData';
import { impactColors } from '../../model/domain/types';
import { formatImpactValue } from '../../../utils/formatters';
import { useAllBaselines } from '../../baseline/hooks/useBaseline';
import { ImpactWithBaseline } from '../../baseline/components/ImpactWithBaseline';
import type { BaselineSummary } from '../../../api/baseline';

/** Format a project's analysis window short label, e.g. "Nov 2019 – Dec 2025". */
function formatAnalysisPeriod(startIso?: string | null, endIso?: string | null): string | null {
    if (!startIso || !endIso) return null;
    const toLabel = (iso: string) => {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso.slice(0, 7);
        return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    };
    return `${toLabel(startIso)} – ${toLabel(endIso)}`;
}

interface ProjectsListProps {
    projects: ProjectTile[];
    isLoading: boolean;
    onProjectClick: (projectId: string, projectName: string) => void;
    onCreateClick?: () => void;
    showCreateItem?: boolean;
    customerId?: string; // Optional
}

export const ProjectsList: React.FC<ProjectsListProps> = ({
    projects,
    isLoading,
    onProjectClick,
    onCreateClick,
    showCreateItem = true,
    customerId
}) => {
    const { token } = useAuth();

    // Use the shared hook instead of duplicating impact logic
    const {
        data: impactData = { projectImpacts: {}, totalImpact: null },
        isLoading: impactLoading
    } = useImpactData(customerId);

    // Fetch all baselines for grid context
    const { data: baselines } = useAllBaselines();
    const baselineMap = useMemo(() => {
        const map: Record<string, BaselineSummary> = {};
        baselines?.forEach(b => { map[b.ba_code] = b; });
        return map;
    }, [baselines]);

    // Update the loading condition to not consider impactLoading if no customerId
    if (isLoading || (customerId && impactLoading)) {
        return (
            <div className="text-center py-8">
                <div className="inline-block animate-spin h-8 w-8 border-4 border-quantum-500 border-t-transparent rounded-full"></div>
                <p className="mt-2 text-text-muted">Loading projects...</p>
            </div>
        );
    }

    if (projects.length === 0 && !showCreateItem) {
        return (
            <div className="bg-surface-700 rounded-lg p-6 text-center text-text-muted border border-border">
                <p>You don't have any projects yet.</p>
                <p>Click 'Create New Project' to get started.</p>
            </div>
        );
    }

    return (
        <div className="bg-surface-700 rounded-lg overflow-hidden border border-border">
            {/* Table header with adjusted spacing */}
            <div className="grid grid-cols-12 bg-surface-600 p-4 border-b border-border font-medium text-text-secondary">
                <div className="col-span-3">Project Name</div>
                <div className="col-span-9 grid grid-cols-4">
                    <div className="flex items-center">
                        <FiZap className="mr-1" style={{ color: impactColors.capacity }} size={14} />
                        <span>Size</span>
                    </div>
                    <div className="flex items-center">
                        <FiHeart className="mr-1" style={{ color: impactColors.health }} size={14} />
                        <span>Health</span>
                    </div>
                    <div className="flex items-center">
                        <FiGlobe className="mr-1" style={{ color: impactColors.species }} size={14} />
                        <span>Biodiversity</span>
                    </div>
                    <div className="flex items-center">
                        <FiDollarSign className="mr-1" style={{ color: impactColors.economic }} size={14} />
                        <span>Economic</span>
                    </div>
                </div>
            </div>

            {/* Project rows with adjusted spacing */}
            <div className="divide-y divide-border">
                {projects.map(project => {
                    // Fix the type error by checking if impactData and projectImpacts exist
                    const impact = impactData?.projectImpacts?.[project.project_id];
                    const hasImpactData = !!impact;
                    const status = project.status;

                    // Baseline lookup for grid context
                    const baseline = project.eia_ba_code ? baselineMap[project.eia_ba_code] : undefined;
                    const gridGenMwh = baseline?.total_generation_mwh;

                    // Per-MWh rates
                    const projGenMwh = impact?.generationMwh;
                    const dalysPerMwh = projGenMwh ? impact.dalys / projGenMwh : null;
                    const pdfsPerMwh = projGenMwh ? impact.pdfs / projGenMwh : null;
                    const econPerMwh = projGenMwh ? impact.economic / projGenMwh : null;

                    // Grid annual totals
                    const gridDalysAnnual = baseline?.human_health_dalys_per_mwh != null && gridGenMwh
                        ? baseline.human_health_dalys_per_mwh * gridGenMwh : null;
                    const gridPdfsAnnual = baseline?.ecosystem_quality_species_yr_per_mwh != null && gridGenMwh
                        ? baseline.ecosystem_quality_species_yr_per_mwh * gridGenMwh : null;
                    const gridUsdAnnual = baseline?.resource_scarcity_usd_per_mwh != null && gridGenMwh
                        ? baseline.resource_scarcity_usd_per_mwh * gridGenMwh : null;

                    // Status badge color
                    const statusBadgeClass = status === "Operating"
                        ? "bg-quantum-500/20 text-quantum-400"
                        : status === "Prospective"
                            ? "bg-economic-500/20 text-economic-400"
                            : "bg-ecosystem-500/20 text-ecosystem-400";

                    return (
                        <div
                            key={project.project_id}
                            className="grid grid-cols-12 p-4 hover:bg-surface-600 items-start cursor-pointer transition-colors"
                            onClick={() => onProjectClick(project.project_id, project.name)}
                        >
                            {/* Project Name with Status / Tech badges + analysis period sub-line */}
                            <div className="col-span-3 pt-0.5">
                                <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                                    <span className="font-medium text-text-primary truncate">{project.name}</span>
                                    <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${statusBadgeClass}`}>
                                        {status}
                                    </span>
                                    {project.tech_type && (
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-surface-500 text-text-secondary flex-shrink-0">
                                            {techLabel(project.tech_type)}
                                        </span>
                                    )}
                                </div>
                                {(() => {
                                    const period = formatAnalysisPeriod(
                                        project.latest_run_start_date,
                                        project.latest_run_end_date,
                                    );
                                    const ba = project.eia_ba_code;
                                    if (!period && !ba) return null;
                                    return (
                                        <div className="text-xs text-text-muted mt-1">
                                            {period && <span>Results: {period}</span>}
                                            {period && ba && <span className="mx-1">·</span>}
                                            {ba && <span>BA: {ba}</span>}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Impact metrics - grouped together in a nested grid */}
                            <div className="col-span-9 grid grid-cols-4">
                                {/* Size (MW Capacity) */}
                                <div>
                                    {hasImpactData ? (
                                        <span className="font-medium" style={{ color: impactColors.capacity }}>
                                            {formatImpactValue(impact.mwCapacity, 'capacity')}
                                        </span>
                                    ) : (
                                        <span className="text-text-muted text-sm italic">—</span>
                                    )}
                                </div>

                                {/* Health Impact (DALYs) */}
                                <div>
                                    {hasImpactData ? (
                                        <ImpactWithBaseline
                                            projectAnnual={impact.dalys}
                                            projectPerMwh={dalysPerMwh}
                                            gridAnnual={gridDalysAnnual}
                                            gridPerMwh={baseline?.human_health_dalys_per_mwh ?? null}
                                            metric="dalys"
                                            colorClass="text-health-400"
                                        />
                                    ) : (
                                        <span className="text-text-muted text-sm italic">—</span>
                                    )}
                                </div>

                                {/* PDFs (Species Saved) */}
                                <div>
                                    {hasImpactData ? (
                                        <ImpactWithBaseline
                                            projectAnnual={impact.pdfs}
                                            projectPerMwh={pdfsPerMwh}
                                            gridAnnual={gridPdfsAnnual}
                                            gridPerMwh={baseline?.ecosystem_quality_species_yr_per_mwh ?? null}
                                            metric="pdfs"
                                            colorClass="text-ecosystem-400"
                                        />
                                    ) : (
                                        <span className="text-text-muted text-sm italic">—</span>
                                    )}
                                </div>

                                {/* Economic Impact */}
                                <div>
                                    {hasImpactData ? (
                                        <ImpactWithBaseline
                                            projectAnnual={impact.economic}
                                            projectPerMwh={econPerMwh}
                                            gridAnnual={gridUsdAnnual}
                                            gridPerMwh={baseline?.resource_scarcity_usd_per_mwh ?? null}
                                            metric="usd"
                                            colorClass="text-economic-400"
                                        />
                                    ) : (
                                        <span className="text-text-muted text-sm italic">—</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Create New Project Row */}
                {showCreateItem && onCreateClick && (
                    <div
                        className="grid grid-cols-12 p-4 hover:bg-surface-600 items-center cursor-pointer text-text-muted hover:text-quantum-400 border-t-2 border-dashed border-border transition-colors"
                        onClick={onCreateClick}
                    >
                        <div className="col-span-3 font-medium flex items-center">
                            <FiPlus className="mr-2 text-lg" />
                            <span>Create New Project</span>
                        </div>
                        <div className="col-span-9"></div>
                    </div>
                )}
            </div>
        </div>
    );
};
